import { Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { ShippingCalculatorService } from '../../../core/services/shipping-calculator.service';
import type { PostcodeSuggestion } from '../../../core/models/shipping-calculator.model';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

/**
 * Suburb/postcode typeahead backed by GET /shipping/postcodes — the static
 * Postcode table, not a live Australia Post lookup (see PostcodesService on
 * the backend for why). Exposes `resolvedPostcode()` for the parent to read
 * via a template reference (e.g. `#from` then `from.resolvedPostcode()`)
 * rather than an output event, since the parent only ever needs the current
 * value at submit time, not a stream of intermediate ones.
 */
@Component({
  selector: 'app-postcode-autocomplete',
  standalone: true,
  templateUrl: './postcode-autocomplete.component.html',
})
export class PostcodeAutocompleteComponent {
  private readonly shippingCalculatorService = inject(ShippingCalculatorService);
  private readonly destroyRef = inject(DestroyRef);

  readonly label = input.required<string>();
  readonly placeholder = input('Suburb or postcode');
  readonly inputId = input<string>();

  readonly query = signal('');
  readonly suggestions = signal<PostcodeSuggestion[]>([]);
  readonly open = signal(false);
  readonly loading = signal(false);

  /** The full picked suggestion (postcode + locality + state), or null if
   * nothing's been chosen from the dropdown yet — unlike resolvedPostcode(),
   * this is never inferred from raw typed digits, since a real suburb/state
   * pairing can't be guessed from a postcode alone (a caller that needs a
   * full address, not just a postcode for a shipping quote, should read
   * this instead). */
  readonly selectedSuggestion = signal<PostcodeSuggestion | null>(null);

  /** The 4-digit postcode to actually use, or null if nothing valid is
   * entered yet — either a clicked suggestion, or exactly 4 digits typed
   * directly (someone who already knows their postcode shouldn't have to
   * wait on/pick from the dropdown). */
  readonly resolvedPostcode = computed(() => {
    const selected = this.selectedSuggestion();
    if (selected) return selected.postcode;
    const typed = this.query().trim();
    return /^\d{4}$/.test(typed) ? typed : null;
  });

  constructor() {
    toObservable(this.query)
      .pipe(
        debounceTime(DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.trim().length < MIN_QUERY_LENGTH) {
            this.suggestions.set([]);
            this.loading.set(false);
            return of(null);
          }
          this.loading.set(true);
          return this.shippingCalculatorService
            .searchPostcodes(q)
            .pipe(catchError(() => of([] as PostcodeSuggestion[])));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.loading.set(false);
        if (results === null) return;
        this.suggestions.set(results);
        this.open.set(results.length > 0);
      });
  }

  onInput(value: string): void {
    this.query.set(value);
    this.selectedSuggestion.set(null);
  }

  select(suggestion: PostcodeSuggestion): void {
    this.selectedSuggestion.set(suggestion);
    this.query.set(`${suggestion.locality}, ${suggestion.state} ${suggestion.postcode}`);
    this.open.set(false);
    this.suggestions.set([]);
  }

  onFocus(): void {
    if (this.suggestions().length) this.open.set(true);
  }

  onBlur(): void {
    this.open.set(false);
  }

  /** Clears the field back to empty — for a caller (e.g. a form) that needs
   * to reset this after a successful submit. */
  reset(): void {
    this.query.set('');
    this.selectedSuggestion.set(null);
    this.suggestions.set([]);
    this.open.set(false);
  }
}
